/**
 * CalendarView — month grid placing rows on their first date column.
 * Drag an event to another day to reschedule; click to open; "+" on a day
 * creates a new row pre-dated to that day.
 */

import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { BoardColumn, BoardFull, BoardRow, StatusOption } from './types';
import { STATUS_COLORS } from './cells';
import { resolveRoles, rowTitle, isoOf, statusOption } from './boardHelpers';

const WD = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

export const CalendarView: React.FC<{
  board: BoardFull;
  dateCol: BoardColumn | null;
  rows: BoardRow[];
  onReschedule: (rowId: string, iso: string) => void;
  onOpenItem: (rowId: string) => void;
  onCreateOn: (iso: string) => void;
  onCreateDateColumn: () => void;
}> = ({ board, dateCol, rows, onReschedule, onOpenItem, onCreateOn, onCreateDateColumn }) => {
  const now = new Date();
  const [cur, setCur] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [drag, setDrag] = useState<string | null>(null);
  const [overDay, setOverDay] = useState<string | null>(null);
  const roles = resolveRoles(board.columns);

  if (!dateCol) {
    return (
      <div className="bd-view-empty">
        La vue Calendrier a besoin d'une colonne de type <strong>Date</strong> pour positionner vos dossiers dans le mois.
        <div style={{ marginTop: 16 }}>
          <button className="bd-btn-primary" onClick={onCreateDateColumn}>
            <PlusIcon className="w-4 h-4" /> Créer une colonne « Date »
          </button>
        </div>
      </div>
    );
  }

  const dated = rows.filter(r => typeof r.values[dateCol.id] === 'string' && r.values[dateCol.id]);
  const statColor = (r: BoardRow): string => {
    const opt = roles.statusCol ? statusOption(roles.statusCol, r.values[roles.statusCol.id]) : null;
    return (opt as StatusOption | null)?.color || 'blue';
  };

  const first = new Date(cur.y, cur.m, 1);
  const monthName = first.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const startOffset = (first.getDay() + 6) % 7;
  const daysIn = new Date(cur.y, cur.m + 1, 0).getDate();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const startDate = new Date(cur.y, cur.m, 1 - startOffset);
  const totalCells = Math.ceil((startOffset + daysIn) / 7) * 7;
  const cells = Array.from({ length: totalCells }, (_, i) => {
    const d = new Date(startDate); d.setDate(startDate.getDate() + i); return d;
  });

  const evFor = (d: Date) => { const iso = isoOf(d); return dated.filter(r => r.values[dateCol.id] === iso); };
  const shift = (n: number) => setCur(c => {
    let m = c.m + n, y = c.y;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    return { y, m };
  });

  return (
    <div className="bd-calwrap">
      <div className="bd-cal-nav">
        <div className="bd-cal-month">{monthName}</div>
        <div className="bd-cal-arrows">
          <button className="bd-icon-btn bd-icon-btn--bordered" onClick={() => shift(-1)}><ChevronLeftIcon className="w-4 h-4" /></button>
          <button className="bd-icon-btn bd-icon-btn--bordered" onClick={() => shift(1)}><ChevronRightIcon className="w-4 h-4" /></button>
        </div>
        <button className="bd-btn bd-cal-today" onClick={() => setCur({ y: now.getFullYear(), m: now.getMonth() })}>Aujourd'hui</button>
      </div>

      <div className="bd-cal">
        <div className="bd-cal-week">{WD.map(w => <div className="bd-cal-wd" key={w}>{w}</div>)}</div>
        <div className="bd-cal-grid">
          {cells.map((d, i) => {
            const out = d.getMonth() !== cur.m;
            const isToday = d.getTime() === today.getTime();
            const iso = isoOf(d);
            const evs = evFor(d);
            return (
              <div
                key={i}
                className={`bd-cal-cell ${out ? 'bd-cal-cell--out' : ''} ${isToday ? 'bd-cal-cell--today' : ''} ${overDay === iso ? 'bd-cal-cell--over' : ''}`}
                onDragOver={e => { if (drag) { e.preventDefault(); setOverDay(iso); } }}
                onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverDay(null); }}
                onDrop={() => { if (drag) onReschedule(drag, iso); setDrag(null); setOverDay(null); }}
              >
                <div className="bd-cal-cell-hd">
                  <div className="bd-cal-daynum">{d.getDate()}</div>
                  <button className="bd-cal-add" title="Nouveau dossier" onClick={() => onCreateOn(iso)}><PlusIcon className="w-3.5 h-3.5" /></button>
                </div>
                {evs.map(r => {
                  const c = statColor(r);
                  const p = STATUS_COLORS[c] || STATUS_COLORS.blue;
                  return (
                    <div
                      key={r.id}
                      className="bd-cal-event"
                      draggable
                      onDragStart={() => setDrag(r.id)}
                      onClick={() => onOpenItem(r.id)}
                      style={{ background: p.bg, color: p.fg }}
                    >
                      <span className="bd-ev-dot" style={{ background: p.fg }} />
                      <span>{rowTitle(r, roles.titleCol)}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
